import produce from 'immer';

import {ProcedureFlowchart, ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {BenchlingAction} from 'redux-stores/Action';

import {ProceduresActionType} from './proceduresActions';

export interface SingleProcedureReduxData {
  flowchart: ProcedureFlowchart;
  // Metadata can only be null during initialization
  procedureMetadata: ProcedureMetadata | null;
  liveProperties: {
    isDirty: boolean;
    isReadOnly: boolean;
    isSaveInFlight: boolean;
    /**
     * The autosaver can be PAUSED as a temporary UX measure. E.g., during sensitive edits that need
     * to own the draft state without a server call.
     *
     * Downstream components own this state, and are fully responsible for managing it.
     */
    isAutosaverPaused: boolean;
    /**
     * The autosaver is considered BLOCKED if there is a fundamental (not temporary) issue. E.g., the
     * procedure is blocked on a server error, etc.
     *
     * This is not something that downstream components should change.
     */
    isAutosaverBlocked: boolean;
  };
}

export type ProceduresState = Record<string, SingleProcedureReduxData>;

export const PROCEDURES_INITIAL_STATE: ProceduresState = {};

// If no existing state is found for a procedure, return a seed empty state
const getSingleProcedureState = (
  existingData: SingleProcedureReduxData | undefined
): SingleProcedureReduxData =>
  existingData ?? {
    flowchart: {nodes: [], edges: []},
    procedureMetadata: null,
    liveProperties: {
      isDirty: false,
      isReadOnly: false,
      isSaveInFlight: false,
      isAutosaverPaused: false,
      isAutosaverBlocked: false,
    },
  };

export default function reduceProcedures(
  state: ProceduresState = PROCEDURES_INITIAL_STATE,
  action: BenchlingAction
): ProceduresState {
  switch (action.type) {
    case ProceduresActionType.UPDATED_FLOWCHART:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        if (action.flowchart != null) {
          draftState[action.procedureId].flowchart = action.flowchart;
        }
        if (action.procedureMetadata != null) {
          draftState[action.procedureId].procedureMetadata = action.procedureMetadata;
        }
      });

    case ProceduresActionType.RELEASED_FLOWCHART:
      return produce(state, (draftState) => {
        delete draftState[action.procedureId];
      });

    case ProceduresActionType.DIRTY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        draftState[action.procedureId].liveProperties.isDirty = action.value;
      });

    case ProceduresActionType.READONLY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        draftState[action.procedureId].liveProperties.isReadOnly = action.value;
      });

    case ProceduresActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        draftState[action.procedureId].liveProperties.isSaveInFlight = action.value;
      });

    case ProceduresActionType.AUTOSAVER_PAUSED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        draftState[action.procedureId].liveProperties.isAutosaverPaused = action.value;
      });

    case ProceduresActionType.AUTOSAVER_BLOCKED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.procedureId] = getSingleProcedureState(draftState[action.procedureId]);
        draftState[action.procedureId].liveProperties.isAutosaverBlocked = action.value;
      });

    default:
      return state;
  }
}
